/** Admin-authored form layouts. A successful inventory read is the authority
 * probe, mirroring navigation: non-admins receive 42501 and never see a control
 * whose save the server would refuse. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PersistedLayoutDocument } from '@solum/experience'
import { useSolum } from '../data/client'

export interface FormLayoutDefault {
  key: string
  entity_type: string
  persona: string | null
  host: string
  layout: PersistedLayoutDocument
  scope: 'tenant' | 'persona' | 'solution' | 'system'
  version: number
  updated_at: string
}

const KEY = ['form-layout-defaults'] as const

/**
 * Every cache an authored definition invalidates — and the reason this is a
 * named list rather than one prefix.
 *
 * These mutations used to invalidate `['experience']`, which reads as "anything
 * to do with experience" and is not what it does. TanStack matches a key
 * PREFIX, element by element, so `['experience']` matches
 * `['experience','authoring-target',…]` and does NOT match
 * `['experience-resolution',…]` — a different first element, not a longer one.
 *
 * The resolution key is the one the RENDERED form reads. So an administrator
 * authored a definition, the toast said it was saved, and the form on screen
 * did not change until the page was reloaded. It was saved; it was simply not
 * shown. Found by a browser cell that only passed once a `page.reload()` was
 * inserted — which is the shape of this class of bug: the test goes green by
 * accommodating it.
 */
const EXPERIENCE_KEYS = ['experience', 'experience-resolution'] as const
/* Two SEPARATE invalidations, one per root. Passing the pair as a single
   `queryKey` asks for the prefix ['experience', 'experience-resolution'],
   which nothing in the app uses — the first attempt at this fix did exactly
   that and changed nothing. */

export function useFormLayoutDefaults(enabled: boolean) {
  const { client } = useSolum()
  return useQuery<FormLayoutDefault[]>({
    queryKey: KEY,
    enabled,
    retry: false,
    queryFn: () => client.raw().rpcTable<FormLayoutDefault>('form_layout_defaults', {}, 'core'),
  })
}

export function useSetFormLayout() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (value: { entity: string; persona: string | null; host: string; layout: PersistedLayoutDocument }) =>
      client.raw().rpcScalar<string>('set_form_layout', {
        p_entity: value.entity,
        p_persona: value.persona,
        p_host: value.host,
        p_layout: value.layout,
      }, 'core'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY })
      for (const key of EXPERIENCE_KEYS) void qc.invalidateQueries({ queryKey: [key] })
    },
  })
}

export function useClearFormLayout() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (value: { entity: string; persona: string | null; host: string }) =>
      client.raw().rpcScalar<boolean>('clear_form_layout', {
        p_entity: value.entity,
        p_persona: value.persona,
        p_host: value.host,
      }, 'core'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY })
      for (const key of EXPERIENCE_KEYS) void qc.invalidateQueries({ queryKey: [key] })
    },
  })
}
